import { BURBANK_GEOID } from '../../eia861UtilityElectricity/bwpObservations';
import {
  BURBANK_CAP_2022_ELECTRICITY,
  BURBANK_LEGACY_CLIMATEPLANS_2022,
} from '../../eia861UtilityElectricity/burbankCapReference';
import {
  buildEia861CanonicalElectricitySeries,
  mwhToKwh,
} from '../../eia861UtilityElectricity/buildEia861CanonicalSeries';
import { EIA861_UTILITY_PILOT_REGISTRY } from '../../eia861UtilityElectricity/registry';
import {
  blendBurbankNonresidentialElectricity,
  blendBurbankIndustrialGas,
  blendBurbankNonresidentialGas,
  blendBurbankResidentialElectricity,
  blendBurbankResidentialGas,
} from './burbankAnalysisInventoryBlend';
import type { BhExperimentLegacyContext } from './legacyContext';
import { legacyValue } from './legacyContext';
import type { BlendedPoint } from './burbankAnalysisInventoryBlend';
import type { ActivityEstimate, CanonicalEnergyActivityYear } from './types';

function est(
  partial: Omit<ActivityEstimate, 'includedCategories' | 'missingCategories'> & {
    includedCategories?: string[];
    missingCategories?: string[];
  }
): ActivityEstimate {
  return {
    includedCategories: partial.includedCategories ?? [],
    missingCategories: partial.missingCategories ?? [],
    ...partial,
  };
}

function fromBlend(blended: BlendedPoint, unit: 'kWh' | 'therms', categories: string[]): ActivityEstimate {
  return est({
    value: blended.value,
    unit,
    coverage: blended.coverage,
    sourceClass: blended.sourceClass,
    methodId: blended.methodId,
    sourceNote: blended.sourceNote,
    includedCategories: categories,
    missingCategories: [],
  });
}

const BURBANK_CONFIG = EIA861_UTILITY_PILOT_REGISTRY[0];

const ANALYSIS_YEARS = Array.from({ length: 2024 - 2005 + 1 }, (_, i) => 2005 + i);

export function buildBurbankElectricityCanonicalSeries(
  legacy: BhExperimentLegacyContext,
  years: number[] = ANALYSIS_YEARS
): CanonicalEnergyActivityYear[] {
  const eia = buildEia861CanonicalElectricitySeries(BURBANK_CONFIG, years);
  const obsByYear = new Map(BURBANK_CONFIG.observations.map((o) => [o.year, o]));

  return eia.map((row) => {
    const raw = obsByYear.get(row.year);
    const comComponentKwh = raw ? mwhToKwh(raw.commercialMwh) : null;
    const indComponentKwh =
      raw?.industrialMwh != null && raw.industrialMwh > 0 ? mwhToKwh(raw.industrialMwh) : null;
    const resBlend = blendBurbankResidentialElectricity(row.year, row);
    const comBlend = blendBurbankNonresidentialElectricity(row.year, row, eia);

    const residential = fromBlend(resBlend, 'kWh', ['residential']);
    const commercial = est({
      ...fromBlend(comBlend, 'kWh', indComponentKwh != null ? ['commercial', 'industrial'] : ['nonresidential']),
      commercialComponent: comBlend.sourceClass === 'cap_published' ? comBlend.value : comComponentKwh ?? comBlend.value,
      industrialComponent: comBlend.sourceClass === 'cap_published' ? null : indComponentKwh,
      sourceNote:
        comBlend.sourceClass === 'cap_published'
          ? comBlend.sourceNote + ' (CAP commercial — not EIA nonresidential)'
          : comBlend.sourceNote,
    });

    const industrial = est({
      value: comBlend.sourceClass === 'cap_published' ? null : indComponentKwh,
      unit: 'kWh',
      coverage: indComponentKwh != null ? residential.coverage : 'extrapolated',
      sourceClass: indComponentKwh != null ? 'utility_observed' : 'modeled',
      methodId: 'burbank_eia861_industrial_component',
      sourceNote:
        comBlend.sourceClass === 'cap_published'
          ? 'CAP commercial year — no EIA sector split'
          : indComponentKwh != null
            ? 'EIA-861 industrial component (included in nonresidential total)'
            : 'Industrial rolled into EIA commercial/nonresidential column for this year',
      includedCategories: indComponentKwh != null ? ['industrial'] : [],
      missingCategories: indComponentKwh == null ? ['industrial'] : [],
    });

    const nonresidential = est({
      value: commercial.value,
      unit: 'kWh',
      coverage: commercial.coverage,
      sourceClass: commercial.sourceClass,
      methodId: commercial.methodId,
      sourceNote: commercial.sourceNote,
      includedCategories: commercial.includedCategories,
      missingCategories: [],
      commercialComponent: commercial.commercialComponent,
      industrialComponent: commercial.industrialComponent,
    });

    const totalVal =
      residential.value != null && nonresidential.value != null
        ? residential.value + nonresidential.value
        : row.totalKwh;

    const total = est({
      value: totalVal,
      unit: 'kWh',
      coverage:
        residential.coverage === 'cap_published' || nonresidential.coverage === 'cap_published'
          ? 'estimated_complete'
          : residential.coverage,
      sourceClass: 'hybrid',
      methodId: 'burbank_analysis_inventory_blend_total',
      sourceNote: 'Residential + nonresidential (CAP and/or EIA-861 per sector rules)',
      includedCategories: ['residential', 'nonresidential'],
      missingCategories: [],
    });

    return {
      geoid: BURBANK_GEOID,
      year: row.year,
      fuel: 'electricity',
      residential,
      commercial,
      industrial,
      agricultural: est({
        value: null,
        unit: 'kWh',
        coverage: 'extrapolated',
        sourceClass: 'modeled',
        methodId: 'n/a',
        sourceNote: 'n/a',
        includedCategories: [],
        missingCategories: ['agricultural'],
      }),
      otherNonresidential: est({
        value: null,
        unit: 'kWh',
        coverage: 'extrapolated',
        sourceClass: 'modeled',
        methodId: 'n/a',
        sourceNote: 'n/a',
        includedCategories: [],
        missingCategories: ['other'],
      }),
      nonresidential,
      total,
      legacyPhysicalResidential: legacyValue(legacy.physicalElecRes, row.year),
      legacyPhysicalCommercial: legacyValue(legacy.physicalElecCom, row.year),
      legacyGhgResidential: legacyValue(legacy.ghgElecRes, row.year),
      legacyGhgCommercial: legacyValue(legacy.ghgElecCom, row.year),
      eia861ReferenceResidentialKwh: row.residential.kwh,
      eia861ReferenceNonresidentialKwh: row.commercial.kwh,
      reconciliation:
        row.year === 2022
          ? {
              residentialPlusNonresidential: totalVal,
              controlTotal: BURBANK_LEGACY_CLIMATEPLANS_2022.totalKwh,
              delta: totalVal - BURBANK_LEGACY_CLIMATEPLANS_2022.totalKwh,
              passes: false,
              note: `CAP 2022 res+com ${BURBANK_CAP_2022_ELECTRICITY.resPlusCommercialKwh.toLocaleString()} kWh; municipal CAP-only ${BURBANK_CAP_2022_ELECTRICITY.municipalInterdepartmentalKwh.toLocaleString()} kWh`,
            }
          : undefined,
    };
  });
}

export function buildBurbankGasCanonicalSeries(
  legacy: BhExperimentLegacyContext,
  years: number[] = ANALYSIS_YEARS
): CanonicalEnergyActivityYear[] {
  return years.map((year) => {
    const resBlend = blendBurbankResidentialGas(year, legacy.physicalGasRes);
    const nonresBlend = blendBurbankNonresidentialGas(year, legacy.physicalGasCom);
    const indBlend = blendBurbankIndustrialGas(year);

    const residential = fromBlend(resBlend, 'therms', ['natural_gas']);
    const commercial = fromBlend(nonresBlend, 'therms', ['natural_gas']);
    const industrial = fromBlend(indBlend, 'therms', ['industrial']);
    const totalVal =
      residential.value != null && commercial.value != null
        ? residential.value + commercial.value
        : null;

    return {
      geoid: BURBANK_GEOID,
      year,
      fuel: 'natural_gas',
      residential,
      commercial,
      industrial,
      agricultural: est({
        value: null,
        unit: 'therms',
        coverage: 'extrapolated',
        sourceClass: 'modeled',
        methodId: 'n/a',
        sourceNote: 'n/a',
        includedCategories: [],
        missingCategories: ['agricultural'],
      }),
      otherNonresidential: est({
        value: null,
        unit: 'therms',
        coverage: 'extrapolated',
        sourceClass: 'modeled',
        methodId: 'n/a',
        sourceNote: 'n/a',
        includedCategories: [],
        missingCategories: ['other'],
      }),
      nonresidential: commercial,
      total: est({
        value: totalVal,
        unit: 'therms',
        coverage: 'estimated_complete',
        sourceClass: 'hybrid',
        methodId: 'burbank_gas_blend_total',
        sourceNote:
          'Residential CAP/SoCal blend + nonresidential CAP/SoCal observed knots with linear interpolation (no modeled gas)',
        includedCategories: ['natural_gas'],
        missingCategories: [],
      }),
      legacyPhysicalResidential: legacyValue(legacy.physicalGasRes, year),
      legacyPhysicalCommercial: legacyValue(legacy.physicalGasCom, year),
      legacyGhgResidential: legacyValue(legacy.ghgGasRes, year),
      legacyGhgCommercial: legacyValue(legacy.ghgGasCom, year),
    };
  });
}
